import {Box, SxProps, Theme} from '@mui/material';
import {useCan} from 'src/permissions/hooks';
import {LocalOfferOutlined} from '@mui/icons-material';
import {ChangeEvent, FC, useEffect, useMemo, useState} from 'react';
import {useIntl} from 'react-intl';
import {businessesApi, tariffsApi} from 'src/api';
import {useBusinessId, useAppSelector} from 'src/app/hooks';
import {store} from 'src/app/store';
import AMPModal from 'src/components/common/AMPModal';
import {TariffForm} from 'src/components/forms/TariffForm';
import {TariffStatsStrip} from 'src/components/tariffs/TariffStatsStrip';
import {TariffsTable} from 'src/components/tariffs/TariffsTable';
import {TariffSortValuesEnum, TariffsStatusEnum} from 'src/enums';
import {preloaderChangeStatus} from 'src/stores/common/commonSlice';
import {DataType, ListParamsType, TariffSearchRequest} from 'src/types';
import {useListQueryState} from 'src/hooks/useListQueryState';
import {isErrorObject} from 'src/utils/helper';
import {NotifyError} from 'src/utils/notification';

type TariffsQueryTrigger = ReturnType<typeof tariffsApi.useLazyGetTariffsQuery>[0];

const DEFAULT_PAGE_SIZE = 100;

/**
 * Everything this screen can be looking at, and the only copy of it: each field lives in the query
 * string, so opening one tariff and coming back restores the view instead of resetting it.
 *
 * `sortField` has no default on purpose - an empty string is not a sort the server knows, and the
 * stats request sends this state as it is. See the note on the request below.
 */
const QUERY_DEFAULTS = {
    page: 0,
    size: DEFAULT_PAGE_SIZE,
    keyword: '',
    status: '',
    country: '',
    region: '',
    sortField: '',
    sortAsc: true,
};

const handleGetTariffs =
    (trigger: TariffsQueryTrigger, businessId?: number) =>
        async (tableData: TariffSearchRequest, params: ListParamsType) => {
            store.dispatch(preloaderChangeStatus(true));

            const newData: DataType = {
                ...tableData,
                businessId: tableData.businessId ? tableData.businessId : businessId,
            };
            Object.keys(tableData).map((item: string) => {
                !newData[item] && delete newData[item];
            });

            if (!tableData.sortField) {
                tableData.sortField = TariffSortValuesEnum.NAME;
            }

            const data: TariffSearchRequest = {
                ...newData,
            };
            try {
                return await trigger({data, params}).unwrap();
            } catch (error) {
                if (isErrorObject(error)) {
                    NotifyError(error.data.message);
                }
            } finally {
                store.dispatch(preloaderChangeStatus(false));
            }
        };

type BusinessesQueryTrigger = ReturnType<typeof businessesApi.useLazyGetBusinessesQuery>[0];

const getBusinessesData = async (trigger: BusinessesQueryTrigger) => {
    try {
        store.dispatch(preloaderChangeStatus(true));
        await trigger({
            params: {
                size: 2000,
            },
        });
    } catch (e) {
        console.log(e);
    } finally {
        store.dispatch(preloaderChangeStatus(false));
    }
};

/**
 * The block and the list are two cards on one page. The top margin is the app bar's: the table used
 * to be first and carried its own, and now that the block is above it the clearance lives here.
 */
const pageSx: SxProps<Theme> = {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    mt: 2.5,
};

export const Tariffs: FC = () => {
    // The business being worked in: the caller's own, or the one a platform administrator picked.
    // Reading `authInfo.business.id` directly was a crash for them - they have no business of
    // their own, and null has no id.
    const businessId = useBusinessId();
    const intl = useIntl();
    const [open, setOpen] = useState<boolean>(false);
    const dialogTitle = intl.formatMessage({id: 'createNewTariff'});
    const {authInfo} = useAppSelector((state) => state.common);

    const [getBusinesses, businesses] = businessesApi.useLazyGetBusinessesQuery();
    const [trigger, response] = tariffsApi.useLazyGetTariffsQuery();
    const [query, setQuery] = useListQueryState(QUERY_DEFAULTS);

    // A boolean rather than `authInfo` itself: the effect below waits for the session to arrive, and
    // depending on the object would re-ask the server every time anything in it was replaced.
    const isSignedIn = Boolean(Object.keys(authInfo).length);

    // Both derived from the address bar rather than kept beside it. No `sortField` until the table
    // sets one: an empty string is not a sort the server knows, and the counts send this state as it
    // is - the list strips empty fields, the counts did not, so opening the page made the stats
    // request fail on `""`.
    const filterParams: TariffSearchRequest = useMemo(() => ({
        businessId: 0,
        keyword: query.keyword,
        status: query.status || undefined,
        country: query.country || undefined,
        region: query.region || undefined,
        sortField: query.sortField || undefined,
        sortAsc: query.sortAsc,
    }), [query]);

    const tableParams: ListParamsType = useMemo(
        () => ({page: query.page, size: query.size}),
        [query.page, query.size]
    );

    const handleOpenModal = () => {
        setOpen(true);
    };
    const handleCloseModal = () => {
        setOpen(false);
        void callHandler(filterParams, tableParams);
    };

    // status is left out: it is what the tiles set, so honouring it would leave each tile showing
    // itself instead of the breakdown. The ordering too - a count has none, and the server drops it.
    const {data: tariffStats} = tariffsApi.useGetTariffStatsQuery({
        ...filterParams,
        businessId: filterParams.businessId || businessId,
        status: undefined,
        sortField: undefined,
    }, {skip: !Object.keys(authInfo).length});

    const handleSelectStatus = (status: TariffsStatusEnum) => {
        setQuery({
            ...query,
            page: 0,
            status: query.status === status ? '' : status,
        });
    };

    /**
     * Whatever the list is now asking for, as a whole request body from the table below.
     *
     * Back to the first page, because the row that was on page four of the old filter is not on page
     * four of the new one.
     */
    const handleChange = (value: TariffSearchRequest) => {
        setQuery({
            ...query,
            page: 0,
            keyword: value.keyword ?? '',
            status: String(value.status ?? ''),
            country: value.country ?? '',
            region: value.region ?? '',
            sortField: value.sortField ?? '',
            sortAsc: value.sortAsc ?? true,
        });
    };

    const handleChangePage = (page: number) => setQuery({...query, page});

    const handleChangeRowsNumber = (e: ChangeEvent<HTMLInputElement>) => setQuery({
        ...query,
        page: 0,
        size: +e.target.value || DEFAULT_PAGE_SIZE,
    });

    // Memoised so the effect below can depend on it: `trigger` is stable and the business only
    // changes when a platform administrator switches tenant, which has to re-ask anyway.
    const callHandler = useMemo(() => handleGetTariffs(trigger, businessId), [trigger, businessId]);

    const canCreateTariff = useCan('tariffs.create');

    /**
     * One place asks the server, whenever the address bar says something else.
     *
     * Every handler above only writes the URL. Before this each of them called the server itself and
     * a mount effect had to put the state back together from `location.state` and a regex over
     * `location.search` - so arriving with a filter already in the URL, which is what Back and a
     * reload both are, fetched the unfiltered list.
     */
    useEffect(() => {
        if (!isSignedIn) {
            return;
        }
        void callHandler(filterParams, tableParams);
    }, [isSignedIn, callHandler, filterParams, tableParams]);

    return (
        <Box sx={pageSx}>
            <TariffStatsStrip
                data={tariffStats}
                activeStatus={filterParams.status}
                onSelectStatus={handleSelectStatus}
            />
            {response.data && (
                <TariffsTable
                    data={response.data}
                    filters={filterParams}
                    size={tableParams.size}
                    pageNumber={tableParams.page}
                    handleUpdateTableData={handleChange}
                    handleChangePage={handleChangePage}
                    handleChangeRowsNumber={handleChangeRowsNumber}
                    title={intl.formatMessage({id: 'tariffs'})}
                    onAddClick={handleOpenModal}
                    buttonText="newTariff"
                    onlyTitle={!canCreateTariff}
                    icon={<LocalOfferOutlined/>}
                />
            )}
            <AMPModal
                handleClose={handleCloseModal}
                open={open}
                title={dialogTitle}
                type="md"
                fullWidth
                withoutPaddings
            >
                <TariffForm
                    businesses={businesses?.data?.content ?? []}
                    handleClose={handleCloseModal}
                    authInfo={authInfo}
                />
            </AMPModal>
        </Box>
    );
};
